import React, { useState } from 'react';
import { Building2, Banknote, HeartHandshake, CheckCircle, Shield, Heart, Car, Home as HomeIcon, Plane, Briefcase, Activity, AlertCircle } from 'lucide-react';

const ServicesSection = () => {
  const [activeCategory, setActiveCategory] = useState('healthcare');
  const [selectedInsurance, setSelectedInsurance] = useState(null);
  const [activeTab, setActiveTab] = useState('sales');

  const healthcareServices = [
    {
      icon: Building2,
      title: 'Cashless Desk Handling',
      color: 'var(--accent-blue-200)',
      iconColor: 'var(--brand-blue)',
      description: 'Comprehensive cashless desk management for hospitals',
      features: [
        'Setting up or managing cashless desk operations',
        'Support for hospitals lacking structured cashless workflow',
        'Process optimization and staff training',
        'Real-time coordination with insurance providers and TPAs'
      ]
    },
    {
      icon: Banknote,
      title: 'Reimbursement Financing - Hospital',
      color: 'var(--accent-green-200)',
      iconColor: 'var(--brand-green)',
      description: 'Hospital-level reimbursement support and financing',
      features: [
        'Support for non-empaneled hospital situations',
        'Interim financing through partnered NBFCs',
        'Detailed policy verification and screening',
        'Lender credit checks and compliant process management'
      ]
    },
    {
      icon: HeartHandshake,
      title: 'Reimbursement Assistance - Patient',
      color: 'var(--accent-pink-200)',
      iconColor: 'var(--brand-blue)',
      description: 'Patient-focused reimbursement claim support',
      features: [
        'End-to-end claim assistance from documentation to follow-up',
        'Guidance on required documents and submission process',
        'Regular claim status updates and tracking',
        'Reducing patient out-of-pocket financial burden'
      ]
    }
  ];

  const insuranceTypes = [
    {
      id: 'health',
      name: 'Health Insurance',
      icon: Heart,
      color: 'bg-red-500',
      subtypes: ['Retail Health', 'Corporate Health', 'Group Mediclaim (GMC)'],
      sales: [
        'Individual Health Plans',
        'Family Floater Policies',
        'Top-up Health Insurance',
        'Critical Illness Riders',
        'Maternity Coverage Plans',
        'Senior Citizen Health Plans',
        'Preventive Health Check-ups',
        'Day Care Procedures Coverage',
        'Corporate Group Health Insurance',
        'Group Mediclaim Policy (GMC)',
        'Employee Health Benefits',
        'Customized Corporate Wellness Plans'
      ],
      advisory: [
        'Health Policy Comparison & Analysis',
        'Coverage Assessment & Gap Analysis',
        'Claim Settlement Assistance',
        'Pre-authorization Support',
        'Cashless Hospitalization Guidance',
        'Policy Renewal Advisory',
        'Network Hospital Information',
        'Premium Optimization Consultation',
        'Medical History Disclosure Guidance',
        'Portability Advisory Services',
        'Copayment & Deductible Consultation',
        'Corporate Health Plan Design'
      ]
    },
    {
      id: 'motor',
      name: 'Motor Insurance',
      icon: Car,
      color: 'bg-blue-500',
      subtypes: ['Private Car', 'Two Wheeler', 'Commercial Vehicle'],
      sales: [
        'Comprehensive Car Insurance',
        'Third-Party Liability Insurance',
        'Two-Wheeler Insurance',
        'Commercial Vehicle Insurance',
        'Zero Depreciation Cover',
        'Engine Protection Cover',
        'Return to Invoice (RTI) Cover',
        'Roadside Assistance Plans',
        'Personal Accident Cover',
        'NCB (No Claim Bonus) Protection',
        'Add-on Covers & Riders',
        'Electric Vehicle Insurance'
      ],
      advisory: [
        'Vehicle Insurance Comparison',
        'IDV (Insured Declared Value) Assessment',
        'Claim Process Guidance',
        'Accident Claim Documentation',
        'Surveyor Coordination Support',
        'NCB Transfer Assistance',
        'Policy Renewal Reminders',
        'Garage & Repair Network Info',
        'Traffic Violation Coverage Advisory',
        'Add-on Cover Recommendations',
        'Premium Discount Optimization',
        'Vehicle Registration Transfer Support'
      ]
    },
    {
      id: 'life',
      name: 'Life Insurance',
      icon: Shield,
      color: 'bg-green-500',
      subtypes: ['Term Life', 'Endowment Plans', 'ULIP', 'Whole Life'],
      sales: [
        'Term Life Insurance Plans',
        'Endowment Life Insurance',
        'Unit Linked Insurance Plans (ULIP)',
        'Whole Life Insurance',
        'Money Back Policies',
        'Child Education Plans',
        'Retirement/Pension Plans',
        'Group Life Insurance',
        'Keyman Insurance',
        'Joint Life Insurance',
        'Single Premium Plans',
        'Online Term Plans'
      ],
      advisory: [
        'Life Cover Assessment',
        'Policy Portfolio Review',
        'Premium Payment Planning',
        'Nominee & Beneficiary Guidance',
        'Policy Loan Assistance',
        'Maturity Planning Advisory',
        'Surrender Value Consultation',
        'Tax Benefit Optimization (80C, 10D)',
        'Claim Settlement Support',
        'Policy Revival Assistance',
        'Rider Addition Recommendations',
        'Estate Planning Guidance'
      ]
    },
    {
      id: 'critical',
      name: 'Critical Illness Insurance',
      icon: Activity,
      color: 'bg-purple-500',
      subtypes: ['Standalone Plans', 'Rider Options'],
      sales: [
        'Standalone Critical Illness Plans',
        'Critical Illness Riders',
        'Cancer-Specific Insurance',
        'Heart Disease Coverage',
        'Stroke Protection Plans',
        'Kidney Failure Coverage',
        'Major Organ Transplant Insurance',
        'Multi-Disease Protection Plans',
        'Lump Sum Benefit Policies',
        'Early Stage Coverage Plans',
        'Family Critical Illness Plans',
        'Income Replacement Plans'
      ],
      advisory: [
        'Disease Coverage Assessment',
        'Policy Comparison & Selection',
        'Pre-existing Condition Consultation',
        'Waiting Period Clarification',
        'Diagnosis Claim Support',
        'Documentation Assistance',
        'Medical Report Coordination',
        'Claim Rejection Resolution',
        'Survival Period Guidance',
        'Payout Utilization Advisory',
        'Policy Renewal Consultation',
        'Rider vs Standalone Comparison'
      ]
    },
    {
      id: 'accidental',
      name: 'Accidental Insurance',
      icon: AlertCircle,
      color: 'bg-orange-500',
      subtypes: ['Personal Accident', 'Group Accident'],
      sales: [
        'Personal Accident Insurance',
        'Group Personal Accident Cover',
        'Accidental Death Benefit',
        'Permanent Total Disability Cover',
        'Permanent Partial Disability Cover',
        'Temporary Total Disability Cover',
        'Medical Expense Reimbursement',
        'Transportation & Ambulance Cover',
        'Education Benefit for Children',
        'Funeral Expense Cover',
        'Daily Hospital Cash',
        'Adventure Sports Coverage'
      ],
      advisory: [
        'Coverage Adequacy Assessment',
        'Accident Claim Process Guidance',
        'Medical Documentation Support',
        'Disability Certification Assistance',
        'Compensation Calculation Help',
        'Exclusion Clarification',
        'Multiple Policy Coordination',
        'Nominee Support Services',
        'Legal Documentation Assistance',
        'Premium Payment Waiver Guidance',
        'Policy Renewal Advisory',
        'Occupational Hazard Consultation'
      ]
    },
    {
      id: 'house',
      name: 'House Insurance',
      icon: HomeIcon,
      color: 'bg-yellow-600',
      subtypes: ['Home Structure', 'Home Contents', 'Comprehensive'],
      sales: [
        'Building Structure Insurance',
        'Home Contents Insurance',
        'Comprehensive Home Insurance',
        'Fire & Allied Perils Coverage',
        'Earthquake & Natural Disaster Cover',
        'Burglary & Theft Protection',
        'Electrical & Mechanical Breakdown',
        'Public Liability Cover',
        'Tenant\'s Insurance',
        'Rent Loss Coverage',
        'Valuable Items Insurance',
        'Home Renovation Cover'
      ],
      advisory: [
        'Property Valuation Guidance',
        'Coverage Assessment',
        'Sum Insured Calculation',
        'Claim Documentation Support',
        'Loss Assessment Coordination',
        'Surveyor Interaction Assistance',
        'Repair & Restoration Guidance',
        'Premium Optimization Tips',
        'Policy Renewal Management',
        'Additional Coverage Recommendations',
        'Exclusions Clarification',
        'Multi-year Policy Benefits'
      ]
    },
    {
      id: 'travel',
      name: 'Travel Insurance',
      icon: Plane,
      color: 'bg-cyan-500',
      subtypes: ['Domestic Travel', 'International Travel', 'Student Travel'],
      sales: [
        'Domestic Travel Insurance',
        'International Travel Insurance',
        'Single Trip Insurance',
        'Multi-Trip Annual Plans',
        'Student Travel Insurance',
        'Senior Citizen Travel Cover',
        'Adventure Travel Insurance',
        'Cruise Travel Insurance',
        'Business Travel Plans',
        'Family Travel Insurance',
        'Schengen Visa Insurance',
        'COVID-19 Travel Coverage'
      ],
      advisory: [
        'Destination-Specific Requirements',
        'Coverage Selection Guidance',
        'Visa Insurance Consultation',
        'Emergency Assistance Services',
        'Medical Evacuation Support',
        'Lost Baggage Claim Help',
        'Trip Cancellation Advisory',
        'Flight Delay Claim Guidance',
        'Passport Loss Assistance',
        '24/7 Helpline Support',
        'Repatriation Coordination',
        'Policy Extension Services'
      ]
    },
    {
      id: 'workmen',
      name: 'Workmen Compensation Insurance',
      icon: Briefcase,
      color: 'bg-indigo-500',
      subtypes: ['Statutory Coverage', 'Enhanced Coverage'],
      sales: [
        'Statutory Workmen Compensation',
        'Enhanced Compensation Plans',
        'Common Law Liability Extension',
        'Employer\'s Liability Coverage',
        'Occupational Disease Cover',
        'Death & Disability Benefits',
        'Medical Expense Coverage',
        'Temporary Disablement Benefits',
        'Funeral Expense Cover',
        'Legal Defense Costs',
        'Rehabilitation Support Cover',
        'Contractual Liability Extension'
      ],
      advisory: [
        'Statutory Compliance Guidance',
        'Premium Calculation Support',
        'Employee Coverage Assessment',
        'Wage Verification Assistance',
        'Claim Notification Process',
        'Injury Documentation Support',
        'Commissioner Filing Guidance',
        'Dispute Resolution Assistance',
        'Policy Renewal Management',
        'Risk Assessment Services',
        'Coverage Extension Advisory',
        'Multi-location Policy Consultation'
      ]
    }
  ];

  return (
    <section id="services" className="section-padding bg-[var(--bg-section)]">
      <div className="container-custom">
        <div className="max-w-3xl mx-auto text-center mb-12">
          <h2 className="heading-1 mb-4">Our Services</h2>
          <p className="body-large text-[var(--text-secondary)]">
            Comprehensive healthcare and insurance solutions for hospitals, insurers, and individuals
          </p>
        </div>

        {/* Category Tabs */}
        <div className="flex justify-center gap-4 mb-8 flex-wrap">
          <button
            onClick={() => {
              setActiveCategory('healthcare');
              setSelectedInsurance(null);
            }}
            className={`px-6 py-3 rounded-full font-medium transition-all ${
              activeCategory === 'healthcare'
                ? 'bg-[var(--text-primary)] text-white'
                : 'bg-white text-[var(--text-primary)] hover:bg-gray-100'
            }`}
            data-testid="healthcare-category-btn"
          >
            Healthcare Services
          </button>
          <button
            onClick={() => setActiveCategory('insurance')}
            className={`px-6 py-3 rounded-full font-medium transition-all ${
              activeCategory === 'insurance'
                ? 'bg-[var(--text-primary)] text-white'
                : 'bg-white text-[var(--text-primary)] hover:bg-gray-100'
            }`}
            data-testid="insurance-category-btn"
          >
            Private Insurance Services
          </button>
        </div>

        {/* Healthcare Services Section */}
        {activeCategory === 'healthcare' && (
          <>
            <div className="grid md:grid-cols-3 gap-6">
              {healthcareServices.map((service, index) => {
                const Icon = service.icon;
                return (
                  <div key={index} className="service-card" style={{ backgroundColor: service.color }}>
                    <div 
                      className="w-14 h-14 rounded-full flex items-center justify-center mb-4"
                      style={{ backgroundColor: 'white' }}
                    >
                      <Icon className="w-7 h-7" style={{ color: service.iconColor }} />
                    </div>
                    <h3 className="heading-3 mb-3">{service.title}</h3>
                    <p className="body-medium text-[var(--text-secondary)] mb-6">
                      {service.description}
                    </p>
                    <ul className="space-y-3">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: service.iconColor }} />
                          <span className="body-small text-[var(--text-secondary)]">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>

            {/* Healthcare CTA */}
            <div className="mt-12 text-center">
              <div className="bg-white rounded-2xl p-8 md:p-12 shadow-sm inline-block max-w-2xl">
                <h3 className="heading-2 mb-4">Need Assistance with Healthcare Claims?</h3>
                <p className="body-medium text-[var(--text-secondary)] mb-6">
                  Our team is ready to help you navigate the complexities of healthcare financing and insurance claims.
                </p>
                <button 
                  onClick={() => {
                    const element = document.getElementById('contact');
                    if (element) {
                      const offset = 80;
                      const elementPosition = element.getBoundingClientRect().top;
                      const offsetPosition = elementPosition + window.pageYOffset - offset;
                      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
                    }
                  }}
                  className="btn-primary"
                >
                  Contact Our Team
                </button>
              </div>
            </div>
          </>
        )}

        {/* Insurance Services Section */}
        {activeCategory === 'insurance' && (
          <>
            {!selectedInsurance ? (
              <>
                <div className="mb-6 text-center">
                  <span className="inline-block px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold">
                    Private Insurance Only - No Government Insurance
                  </span>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {insuranceTypes.map((insurance) => {
                    const Icon = insurance.icon;
                    return (
                      <div
                        key={insurance.id}
                        onClick={() => setSelectedInsurance(insurance)}
                        className="cursor-pointer bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 p-6 border-2 border-transparent hover:border-blue-200"
                        data-testid={`insurance-card-${insurance.id}`}
                      >
                        <div className={`${insurance.color} w-14 h-14 rounded-lg flex items-center justify-center mb-4`}>
                          <Icon className="w-8 h-8 text-white" />
                        </div>
                        <h3 className="heading-3 mb-2">{insurance.name}</h3>
                        <div className="space-y-1">
                          {insurance.subtypes.map((subtype, idx) => (
                            <p key={idx} className="body-small text-[var(--text-secondary)]">• {subtype}</p>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            ) : (
              <>
                {/* Back Button */}
                <button
                  onClick={() => setSelectedInsurance(null)}
                  className="mb-6 px-4 py-2 bg-white rounded-lg text-[var(--text-primary)] hover:bg-gray-100 transition-all flex items-center gap-2"
                  data-testid="back-btn"
                >
                  ← Back to Insurance Types
                </button>

                {/* Insurance Details */}
                <div className="bg-white rounded-xl shadow-lg p-8" data-testid="insurance-details">
                  <div className="flex items-center gap-4 mb-6">
                    {React.createElement(selectedInsurance.icon, {
                      className: `w-12 h-12 ${selectedInsurance.color.replace('bg-', 'text-')}`
                    })}
                    <div>
                      <h2 className="heading-1">{selectedInsurance.name}</h2>
                      <p className="body-medium text-[var(--text-secondary)] mt-1">Comprehensive Service Categories</p>
                    </div>
                  </div>

                  {/* Service Tabs */}
                  <div className="flex gap-4 mb-6 border-b border-gray-200">
                    <button
                      onClick={() => setActiveTab('sales')}
                      className={`px-6 py-3 font-semibold transition-all ${
                        activeTab === 'sales'
                          ? 'text-blue-600 border-b-2 border-blue-600'
                          : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
                      }`}
                      data-testid="sales-tab"
                    >
                      Sales Services ({selectedInsurance.sales.length})
                    </button>
                    <button
                      onClick={() => setActiveTab('advisory')}
                      className={`px-6 py-3 font-semibold transition-all ${
                        activeTab === 'advisory'
                          ? 'text-green-600 border-b-2 border-green-600'
                          : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
                      }`}
                      data-testid="advisory-tab"
                    >
                      Advisory Services ({selectedInsurance.advisory.length})
                    </button>
                  </div>

                  {/* Service Categories */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {activeTab === 'sales' &&
                      selectedInsurance.sales.map((service, idx) => (
                        <div
                          key={idx}
                          className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4 hover:shadow-md transition-shadow"
                          data-testid={`sales-service-${idx}`}
                        >
                          <div className="flex items-start gap-3">
                            <div className="w-2 h-2 bg-blue-600 rounded-full mt-2 flex-shrink-0"></div>
                            <p className="body-small font-medium text-[var(--text-primary)]">{service}</p>
                          </div>
                        </div>
                      ))}
                    {activeTab === 'advisory' &&
                      selectedInsurance.advisory.map((service, idx) => (
                        <div
                          key={idx}
                          className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4 hover:shadow-md transition-shadow"
                          data-testid={`advisory-service-${idx}`}
                        >
                          <div className="flex items-start gap-3">
                            <div className="w-2 h-2 bg-green-600 rounded-full mt-2 flex-shrink-0"></div>
                            <p className="body-small font-medium text-[var(--text-primary)]">{service}</p>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </section>
  );
};

export default ServicesSection;
